import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const services = [
  { title: 'Diagnóstico de Sistemas Hidráulicos', desc: 'Avaliação técnica completa de sistemas hidráulicos em máquinas florestais, colhedoras, processadores e equipamentos de celulose em Linhares.' },
  { title: 'Substituição de Componentes', desc: 'Troca de componentes hidráulicos desgastados ou danificados com peças de qualidade equivalente para ambientes florestais e agroindustriais.' },
  { title: 'Troca de Mangueiras e Conexões', desc: 'Substituição de mangueiras hidráulicas e conexões em sistemas expostos a poeira de madeira, resíduos de celulose e ambiente agroindustrial.' },
  { title: 'Ajustes Operacionais', desc: 'Calibração e ajustes de parâmetros operacionais para otimização de performance em máquinas florestais e equipamentos de processamento.' },
  { title: 'Inspeções Hidráulicas Preventivas', desc: 'Inspeções preventivas para identificar condições e prever necessidades de intervenção em sistemas críticos de celulose e florestal.' },
  { title: 'Correção de Vazamentos', desc: 'Identificação e reparo de vazamentos em conexões, vedações, mangueiras e componentes hidráulicos em ambiente florestal e agroindustrial.' },
  { title: 'Montagem e Conexão de Sistemas', desc: 'Montagem e conexão de sistemas hidráulicos seguindo especificações técnicas e normas de segurança em operações florestais e industriais.' },
  { title: 'Apoio Técnico Especializado', desc: 'Suporte técnico especializado em campo para diagnóstico, resolução e consultoria hidráulica em celulose, florestal e agroindústria.' },
];

const segments = [
  'Celulose',
  'Indústria Florestal',
  'Agroindústria',
  'Logística',
  'Indústria de Transformação',
  'Processamento de Madeira',
];

const differentials = [
  'Atendimento rápido para Linhares e região norte do Espírito Santo',
  'Experiência em sistemas hidráulicos de máquinas florestais e equipamentos de celulose',
  'Equipe técnica especializada em diagnóstico e intervenção em campo florestal',
  'Logística otimizada para indústrias de celulose e florestal',
  'Parceiros homologados para componentes resistentes a ambiente florestal e agroindustrial',
  'Foco em segurança, confiabilidade e conformidade normativa',
];

const faqs = [
  { q: 'A Pilar atende diretamente em Linhares?', a: 'Sim, a Pilar realiza atendimento diretamente na região de Linhares, com equipe de campo própria para diagnóstico, intervenção e suporte técnico em sistemas hidráulicos industriais de celulose e florestal.' },
  { q: 'Quais tipos de sistemas hidráulicos são atendidos em Linhares?', a: 'Sistemas hidráulicos de máquinas florestais, colhedoras, processadores, equipamentos de celulose, prensas industriais, transportadores e sistemas agroindustriais.' },
  { q: 'A Pilar trabalha em paradas de manutenção programadas?', a: 'Sim, a Pilar participa ativamente de paradas de manutenção programadas em indústrias de celulose e florestal, com equipe escalada e planejamento de atividades conforme cronograma do cliente.' },
];

export default function LinharesHidraulicaPage() {
  useSeo({
    title: 'Hidráulica Industrial em Linhares | Serviços de Campo | Pilar',
    description: 'Serviços de hidráulica industrial em Linhares e região. Manutenção, diagnóstico e recuperação de sistemas hidráulicos para celulose, florestal, agroindústria, logística e indústria de transformação do norte do Espírito Santo.',
    keywords: 'hidráulica industrial Linhares, manutenção hidráulica Linhares, serviços hidráulicos celulose ES, sistemas hidráulicos florestal Linhares, reparo hidráulico agroindústria ES, Grupo Pilar Linhares',
    canonical: 'https://www.pilarmanutencao.com.br/hidraulica-industrial-linhares',
    ogUrl: 'https://www.pilarmanutencao.com.br/hidraulica-industrial-linhares',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/LOCAL/LINHARES/linhares-hidraulica-hero.jpg"
              alt="Hidráulica industrial em Linhares - Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Linhares - Norte do Espírito Santo</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Hidráulica</span> Industrial em Linhares
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Serviços de hidráulica industrial em campo para celulose, florestal, agroindústria, logística e indústria de transformação da região de Linhares.
              </p>
            </div>
          </div>
        </section>

        {/* Introdução Local */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Atuação em Linhares e Região</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Linhares é um importante polo de celulose, florestal e agroindústria no norte do Espírito Santo. A Pilar oferece serviços de hidráulica industrial em campo, com equipe técnica especializada para atender sistemas hidráulicos de máquinas florestais, colhedoras, processadores e equipamentos de celulose.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Atuamos em diagnóstico, manutenção preventiva, corretiva e intervenções emergenciais em campo, com logística otimizada para a região de Linhares.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Principais Serviços de Hidráulica em Linhares</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Segmentos */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Segmentos Atendidos em Linhares</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{segment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferenciais */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Diferenciais da Pilar para Linhares</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {differentials.map((diff, index) => (
                <div key={index} className="flex items-center gap-3 bg-gp-graphite border border-white/10 rounded-lg px-4 py-3 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{diff}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Perguntas Frequentes</h2>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-gp-graphite border border-white/10 rounded-lg p-6">
                    <h3 className="font-display text-base font-bold text-white uppercase mb-3">{faq.q}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de apoio em hidráulica industrial em Linhares?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Solicite uma avaliação técnica e conte com o Grupo Pilar para apoiar sua operação com serviços hidráulicos de campo na região de Linhares.
                </p>
                <a href="https://wa.me/5527993092922" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap">
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar Apoio Hidráulico</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Links Internos */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link to="/hidraulica-industrial" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Página Principal do Serviço
              </Link>
              <Link to="/contato" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                Contato
              </Link>
              <a href="https://wa.me/5527993092922" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}